/**
 * BaseContentService
 *
 * Abstract base class for content services that follow the 10-step integration pattern.
 * Eliminates 60-70% code duplication across Document, Memory, Note, and ResearchNote services.
 *
 * Features:
 * - Standardized 10-step pattern (config, feature flags, validation, limits, approval, etc.)
 * - Shared approval workflow (approve, reject, getAwaitingApproval)
 * - Common CRUD operations with permission checks
 * - Helper methods (timestamp conversion, field cleanup)
 * - Type-safe with generics
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit as limitQuery,
  QueryConstraint,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { SystemConfig } from '../types';
import { SystemConfigService } from './systemConfigService';
import { ChangeTrackingService } from './changeTrackingService';
import { AuditLogService } from './auditLogService';
import { NotificationService } from './notificationService';
import { NotificationPriority } from '../types/user';

/**
 * Base entity interface that all content types must extend
 */
export interface BaseContentEntity {
  id: string;
  personId: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  isApproved: boolean;
  isDeleted: boolean;
  [key: string]: any; // Allow additional fields
}

/**
 * Options for content creation
 */
export interface CreateContentOptions {
  requiresApproval: boolean;
  isPrivate?: boolean;
  entityTypeName: string;
  skipNotification?: boolean;
}

/**
 * Abstract base class for content services
 */
export abstract class BaseContentService<T extends BaseContentEntity> {
  // ============================================================================
  // ABSTRACT PROPERTIES (must be defined by subclasses)
  // ============================================================================

  /** Firestore collection name */
  protected abstract collectionName: string;

  /** Feature flag key in SystemConfig */
  protected abstract featureFlagKey: keyof SystemConfig['features'];

  /** Approval config key (e.g., 'documentsRequireApproval') */
  protected abstract approvalConfigKey?: keyof SystemConfig['content'];

  /** Max per person config key (e.g., 'maxDocumentsPerPerson') */
  protected abstract maxPerPersonConfigKey?: keyof SystemConfig['content'];

  /** Author field name in the entity ('authorId' | 'uploadedBy' | 'researcherId') */
  protected abstract authorFieldName: string;

  /** Entity type for change tracking ('document' | 'memory' | 'note' | 'research') */
  protected abstract changeTrackingEntityType: string;

  // ============================================================================
  // ABSTRACT METHODS (must be implemented by subclasses)
  // ============================================================================

  /**
   * Validate content-specific data
   */
  protected abstract validateContent(data: any, config: SystemConfig): Promise<void>;

  /**
   * Build Firestore query constraints from filters
   */
  protected abstract buildFilters(filters: any): QueryConstraint[];

  /**
   * Map Firestore document data to typed entity
   */
  protected abstract mapDocumentToEntity(docData: any, docId: string): T;

  // ============================================================================
  // SHARED 10-STEP PATTERN METHOD
  // ============================================================================

  /**
   * Execute the standardized 10-step content creation pattern
   */
  protected async executeCreatePattern(
    data: any,
    userId: string,
    userName: string,
    userRole: string,
    options: CreateContentOptions
  ): Promise<T> {
    // STEP 1: Get SystemConfig
    const config = await SystemConfigService.getConfig();

    // STEP 2: Check feature flags & permissions
    this.checkFeatureEnabled(config);
    this.checkRolePermission(userRole);

    // STEP 3: Validate content (delegated to subclass)
    await this.validateContent(data, config);

    // STEP 4: Check per-entity limits
    await this.checkPerPersonLimit(data.personId, config);

    // STEP 5: Determine approval status
    const requiresApproval = this.determineApprovalStatus(
      userRole,
      config,
      options.requiresApproval
    );

    // STEP 6: Perform Firebase operation
    const entityData = {
      ...data,
      [this.authorFieldName]: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userId,
      isApproved: !requiresApproval,
      isDeleted: false,
    };

    const docRef = await addDoc(
      collection(db, this.collectionName),
      this.removeUndefinedFields(entityData)
    );

    const createdEntity = {
      ...entityData,
      id: docRef.id,
    } as T;

    // STEP 7: Log to ChangeTrackingService
    await ChangeTrackingService.logChange(
      {
        entityType: this.changeTrackingEntityType as any,
        entityId: docRef.id,
        changeType: 'create',
        beforeState: null,
        afterState: createdEntity,
      },
      userId,
      userName,
      !requiresApproval
    );

    // STEP 8: Log to AuditLogService
    await AuditLogService.logAction({
      userId,
      action: 'content_approved',
      contentType: this.changeTrackingEntityType as any,
      contentId: docRef.id,
      details: {
        personId: data.personId,
        requiresApproval,
        userName,
      },
    });

    // STEP 9: Send email notification (if needed and not skipped)
    if (!options.skipNotification && requiresApproval) {
      // TODO: Implement email notification service
      console.log(`Email notification: ${options.entityTypeName} awaiting approval`);
    }

    // STEP 10: Return result
    return createdEntity;
  }

  // ============================================================================
  // SHARED APPROVAL WORKFLOW
  // ============================================================================

  /**
   * Approve pending content
   */
  async approve(
    entityId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    // Only admins can approve
    if (userRole !== 'admin') {
      throw new Error('Only administrators can approve content');
    }

    const docRef = doc(db, this.collectionName, entityId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error(`${this.changeTrackingEntityType} not found`);
    }

    const entity = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

    if (entity.isApproved) {
      throw new Error(`${this.changeTrackingEntityType} is already approved`);
    }

    // Update approval status
    await updateDoc(docRef, {
      isApproved: true,
      approvedAt: new Date(),
      approvedBy: userId,
      updatedAt: new Date(),
    });

    // Log to ChangeTrackingService
    await ChangeTrackingService.logChange(
      {
        entityType: this.changeTrackingEntityType as any,
        entityId,
        changeType: 'update',
        beforeState: { isApproved: false },
        afterState: { isApproved: true },
      },
      userId,
      userName,
      true
    );

    // Log to AuditLogService
    await AuditLogService.logAction({
      userId,
      action: 'content_approved',
      contentType: this.changeTrackingEntityType as any,
      contentId: entityId,
      details: {
        personId: entity.personId,
        userName,
      },
    });

    // Send approval notification to content creator (non-blocking)
    if (entity.createdBy && entity.createdBy !== userId) {
      NotificationService.createNotification({
        userId: entity.createdBy,
        type: 'content_approved',
        data: {
          contentType: this.changeTrackingEntityType,
          contentId: entityId,
          approvedBy: userName,
        },
      }).catch(err => console.error('Failed to create content approval notification:', err));
    }
  }

  /**
   * Reject pending content
   */
  async reject(
    entityId: string,
    reason: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    // Only admins can reject
    if (userRole !== 'admin') {
      throw new Error('Only administrators can reject content');
    }

    const docRef = doc(db, this.collectionName, entityId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error(`${this.changeTrackingEntityType} not found`);
    }

    const entity = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

    if (entity.isApproved) {
      throw new Error(`${this.changeTrackingEntityType} is already approved`);
    }

    // Soft delete rejected content
    await updateDoc(docRef, {
      isDeleted: true,
      rejectedAt: new Date(),
      rejectedBy: userId,
      rejectionReason: reason,
      updatedAt: new Date(),
    });

    // Log to AuditLogService
    await AuditLogService.logAction({
      userId,
      action: 'content_rejected',
      contentType: this.changeTrackingEntityType as any,
      contentId: entityId,
      details: {
        personId: entity.personId,
        reason,
        userName,
      },
    });

    // Send rejection notification to content creator (non-blocking)
    if (entity.createdBy && entity.createdBy !== userId) {
      NotificationService.createNotification({
        userId: entity.createdBy,
        type: 'content_rejected',
        data: {
          contentType: this.changeTrackingEntityType,
          contentId: entityId,
          rejectedBy: userName,
          reason,
        },
      }).catch(err => console.error('Failed to create content rejection notification:', err));
    }
  }

  /**
   * Get content awaiting approval
   */
  async getAwaitingApproval(limit: number = 50): Promise<T[]> {
    const q = query(
      collection(db, this.collectionName),
      where('isApproved', '==', false),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc'),
      limitQuery(limit)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) =>
      this.mapDocumentToEntity(doc.data(), doc.id)
    );
  }

  // ============================================================================
  // SHARED CRUD OPERATIONS
  // ============================================================================

  /**
   * Get entity by ID
   */
  async getById(entityId: string, userId?: string): Promise<T | null> {
    const docRef = doc(db, this.collectionName, entityId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const entity = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

    // Check if deleted
    if (entity.isDeleted) {
      return null;
    }

    // If not approved, only show to creator or admins
    if (!entity.isApproved && userId) {
      const authorId = (entity as any)[this.authorFieldName];
      if (authorId !== userId) {
        // TODO: Check if user is admin
        return null;
      }
    }

    return entity;
  }

  /**
   * Get entities by person ID
   */
  async getByPerson(
    personId: string,
    filters?: any,
    userId?: string
  ): Promise<T[]> {
    const constraints: QueryConstraint[] = [
      where('personId', '==', personId),
      where('isDeleted', '==', false),
    ];

    // Add user-specific filters
    if (filters) {
      constraints.push(...this.buildFilters(filters));
    }

    // By default, only show approved content (unless user is viewing their own)
    if (!filters?.includeUnapproved) {
      constraints.push(where('isApproved', '==', true));
    }

    constraints.push(orderBy('createdAt', 'desc'));

    const q = query(collection(db, this.collectionName), ...constraints);
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      this.mapDocumentToEntity(doc.data(), doc.id)
    );
  }

  /**
   * Soft delete entity
   */
  async softDelete(
    entityId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    const docRef = doc(db, this.collectionName, entityId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error(`${this.changeTrackingEntityType} not found`);
    }

    const entity = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

    // Check ownership or admin
    if (!this.checkOwnerOrAdmin(entity, userId, userRole)) {
      throw new Error('You do not have permission to delete this content');
    }

    // Soft delete
    await updateDoc(docRef, {
      isDeleted: true,
      deletedAt: new Date(),
      deletedBy: userId,
      updatedAt: new Date(),
    });

    // Log to ChangeTrackingService
    await ChangeTrackingService.logChange(
      {
        entityType: this.changeTrackingEntityType as any,
        entityId,
        changeType: 'delete',
        beforeState: { isDeleted: false },
        afterState: { isDeleted: true },
      },
      userId,
      userName,
      true
    );

    // Log to AuditLogService
    await AuditLogService.logAction({
      userId,
      action: 'content_deleted',
      contentType: this.changeTrackingEntityType as any,
      contentId: entityId,
      details: {
        personId: entity.personId,
        userName,
      },
    });
  }

  // ============================================================================
  // SHARED PERMISSION CHECKS
  // ============================================================================

  /**
   * Check if feature is enabled
   */
  protected checkFeatureEnabled(config: SystemConfig): void {
    const featureName = this.featureFlagKey as string;
    const isEnabled = config.features[this.featureFlagKey];

    if (!isEnabled) {
      throw new Error(`${featureName} feature is currently disabled`);
    }
  }

  /**
   * Check role permission
   */
  protected checkRolePermission(
    userRole: string,
    allowedRoles: string[] = ['contributor', 'editor', 'admin']
  ): void {
    if (!allowedRoles.includes(userRole.toLowerCase())) {
      throw new Error(
        `Insufficient permissions. Required: ${allowedRoles.join(', ')}`
      );
    }
  }

  /**
   * Check if user is owner or admin
   */
  protected checkOwnerOrAdmin(
    entity: T,
    userId: string,
    userRole: string
  ): boolean {
    const authorId = (entity as any)[this.authorFieldName];
    return authorId === userId || userRole === 'admin';
  }

  /**
   * Check per-person entity limit
   */
  protected async checkPerPersonLimit(
    personId: string,
    config: SystemConfig
  ): Promise<void> {
    if (!this.maxPerPersonConfigKey) {
      return; // No limit configured
    }

    const maxAllowed = config.content[this.maxPerPersonConfigKey] as number;
    if (!maxAllowed || maxAllowed === 0) {
      return; // Unlimited
    }

    // Count existing entities for this person
    const q = query(
      collection(db, this.collectionName),
      where('personId', '==', personId),
      where('isDeleted', '==', false)
    );

    const querySnapshot = await getDocs(q);
    const count = querySnapshot.size;

    if (count >= maxAllowed) {
      throw new Error(
        `Maximum ${maxAllowed} ${this.collectionName} per person exceeded`
      );
    }
  }

  /**
   * Determine if content requires approval
   */
  protected determineApprovalStatus(
    userRole: string,
    config: SystemConfig,
    defaultRequiresApproval: boolean
  ): boolean {
    // Admins and editors bypass approval
    if (userRole === 'admin' || userRole === 'editor') {
      return false;
    }

    // Check config if approval key is defined
    if (this.approvalConfigKey) {
      return config.content[this.approvalConfigKey] as boolean;
    }

    // Fall back to default
    return defaultRequiresApproval;
  }

  // ============================================================================
  // SHARED HELPER METHODS
  // ============================================================================

  /**
   * Convert Firestore Timestamp to Date
   */
  protected timestampToDate(timestamp: any): Date {
    if (!timestamp) {
      return new Date();
    }

    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }

    if (timestamp instanceof Date) {
      return timestamp;
    }

    if (typeof timestamp === 'string') {
      return new Date(timestamp);
    }

    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }

    return new Date();
  }

  /**
   * Remove undefined fields from object (Firebase doesn't accept undefined)
   */
  protected removeUndefinedFields(obj: any): any {
    const cleaned: any = {};

    Object.keys(obj).forEach((key) => {
      if (obj[key] !== undefined) {
        cleaned[key] = obj[key];
      }
    });

    return cleaned;
  }
}
